import { z } from 'zod'
import { WorkflowConditionConfigSchema } from '@omnea-app/packages/universal/conditionality/schema'
import { UserConditionConfigSchema } from '@omnea-app/packages/universal/conditionality/userCondition'
import { ref } from '@omnea-app/packages/universal/references/refKinds'
import type { PrimitiveType } from '@omnea-digital/data-variables'

// declare-config-references allow: sourceId -- names its target, so there is no row id to record
// declare-config-references allow: blockId -- a block of this same document
// declare-config-references allow: source -- an object; its reference is declared inside
// declare-config-references allow: outputId -- the variable this writes, not one it reads

// Every type here infers from its schema: a hand-written type beside one lets a field reach the
// compiler and not the walk, and a field the schema omits is walked in silence.

const TRANSFORMATION_OUTPUT_PRIMITIVES = ['number', 'text', 'boolean', 'entity'] as const satisfies readonly PrimitiveType[]

const TransformationOutputPrimitiveSchema = z.enum(TRANSFORMATION_OUTPUT_PRIMITIVES)
export type TransformationOutputPrimitive = z.infer<typeof TransformationOutputPrimitiveSchema>

const FormFieldReferenceSchema = z.object({ sourceType: z.literal('form-field'), sourceId: z.string() })

const VariableReferenceSchema = z.discriminatedUnion('sourceType', [
  FormFieldReferenceSchema,
  z.object({ sourceType: z.literal('request-context'), sourceId: z.string() }),
  // Values computed at execution time that depend on neither the request nor a previous
  // block (e.g. Today). `sourceId` is a ReferenceValueId — see referenceValues.ts.
  z.object({ sourceType: z.literal('reference-value'), sourceId: z.string() }),
  z.object({
    sourceType: z.literal('block-output'),
    sourceId: z.string(),
    blockId: z.string(),
    blockName: z.string(),
  }),
  // Used only by External Operation custom primary output. `sourceId` is the output
  // mapping key (= form question name); extops resolves it via `outputMapping[sourceId]`.
  z.object({ sourceType: z.literal('output-field'), sourceId: z.string() }),
])
export type VariableReference = z.infer<typeof VariableReferenceSchema>

// A static value under an entity output holds a core data option id, scoped by `outputEntitySource`.
// It stays undeclared because this same arg carries plain match values everywhere else, and
// annotating it would report each of those as an option for config-sync to remap.
const TransformationArgSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('variable'), source: VariableReferenceSchema }),
  z.object({ kind: z.literal('static'), value: z.string() }),
])
export type TransformationArg = z.infer<typeof TransformationArgSchema>

// Normalised against a dynamic option's `dataSource` before comparison, so it names a core data set
// by the same value a question does.
const OutputEntitySourceSchema = ref('coreDataSet').optional()

const TransformationRuleSchema = z.object({
  id: z.string(),
  when: TransformationArgSchema,
  then: TransformationArgSchema,
})
export type TransformationRule = z.infer<typeof TransformationRuleSchema>

const SwitchBranchSchema = z.object({
  id: z.string(),
  when: WorkflowConditionConfigSchema,
  then: TransformationArgSchema,
})
export type SwitchBranch = z.infer<typeof SwitchBranchSchema>

/** A condition evaluated against something other than the request; `subject` names what against. */
const SubjectPredicateSchema = z.object({ subject: z.literal('user'), condition: UserConditionConfigSchema })
export type SubjectPredicate = z.infer<typeof SubjectPredicateSchema>

// Among the request-context fields only the requestor resolves to a user id, so the rest are excluded
// here rather than left to fail at runtime.
const ManagerWalkStartSchema = z.union([
  FormFieldReferenceSchema,
  z.object({ sourceType: z.literal('request-context'), sourceId: z.literal('request.requestor') }),
])
export type ManagerWalkStart = z.infer<typeof ManagerWalkStartSchema>

const TransformationValueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('function'), functionName: z.string(), args: z.array(TransformationArgSchema) }),
  z.object({
    kind: z.literal('rules'),
    subject: TransformationArgSchema,
    rules: z.array(TransformationRuleSchema),
    defaultValue: TransformationArgSchema.optional(),
    outputPrimitive: TransformationOutputPrimitiveSchema.optional(),
    outputEntitySource: OutputEntitySourceSchema,
  }),
  z.object({
    // Primitive and shape are never stored: the editor derives them from the source variable and the
    // runtime from the resolved value, so nothing here is the place to look for them.
    kind: z.literal('reference'),
    source: VariableReferenceSchema,
  }),
  z.object({
    kind: z.literal('switch'),
    branches: z.array(SwitchBranchSchema),
    defaultValue: TransformationArgSchema.optional(),
    outputPrimitive: TransformationOutputPrimitiveSchema.optional(),
    outputEntitySource: OutputEntitySourceSchema,
    // When true, evaluate every branch (not just the first match) and emit a
    // text/list of the matching `then` values — used to populate multi-select
    // dropdowns. Falsy/absent preserves the original first-match behaviour.
    collectAll: z.boolean().optional(),
  }),
  z.object({
    // Copies/merges file attachments from one-or-many source variables into a
    // single file/list output. Each source is resolved to its native
    // VariableValue; only file-primitive results are kept, their files
    // concatenated in source order and de-duplicated by fileStorageId.
    // Non-file or unresolvable sources are skipped (suppliers may leave
    // upload slots empty), never thrown on.
    kind: z.literal('copy-files'),
    sources: z.array(VariableReferenceSchema),
  }),
  z.object({
    // Emits the nearest manager above the start user satisfying `predicate`; a no-match skips the output.
    kind: z.literal('manager-walk'),
    start: ManagerWalkStartSchema,
    predicate: SubjectPredicateSchema,
  }),
])
export type TransformationValue = z.infer<typeof TransformationValueSchema>

export const TransformationOutputFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  outputId: z.string(),
  transformation: TransformationValueSchema,
})
export type TransformationOutputField = z.infer<typeof TransformationOutputFieldSchema>
